import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  HiOutlineTrash,
  HiOutlinePlus,
  HiOutlineMinus,
  HiOutlineArrowLeft,
  HiOutlineExclamationTriangle,
  HiOutlineMapPin,
  HiOutlineCheckCircle,
  HiOutlinePhone,
  HiSparkles,
  HiOutlineTicket,
  HiOutlineXMark,
} from "react-icons/hi2";
import { Tag } from "lucide-react";

import { RiShoppingBag3Line } from "react-icons/ri";
import toast, { Toaster } from "react-hot-toast";
import SummaryApi from "../common";
import UserAddress from "../pages/UserAddress";
import {
  getSubscriptionDetails,
  calculateDiscountAmount,
} from "../utils/subscriptionDiscount";

const RemovedToast = ({ t, productName }) => (
  <div
    className={`${
      t.visible ? "animate-enter" : "animate-leave"
    } max-w-md w-full bg-white shadow-2xl rounded-[1.5rem] pointer-events-auto flex ring-1 ring-black ring-opacity-5 overflow-hidden border border-rose-100`}
  >
    <div className="flex-1 w-0 p-4">
      <div className="flex items-center">
        <div className="flex-shrink-0 pt-0.5">
          <div className="h-12 w-12 rounded-xl bg-rose-500 flex items-center justify-center text-white shadow-lg shadow-rose-200">
            <HiOutlineTrash size={24} />
          </div>
        </div>
        <div className="ml-4 flex-1">
          <p className="text-sm font-black text-rose-950 uppercase tracking-tight">
            Removed from box
          </p>
          <p className="mt-1 text-xs font-medium text-rose-900/40">
            {productName} removed.
          </p>
        </div>
      </div>
    </div>
    <div className="flex border-l border-rose-50">
      <button
        onClick={() => toast.dismiss(t.id)}
        className="w-full border border-transparent p-4 flex items-center justify-center text-sm font-black text-rose-500 hover:text-rose-600 focus:outline-none"
      >
        Close
      </button>
    </div>
  </div>
);

const OrderPlacedToast = ({ t }) => (
  <div
    className={`${
      t.visible ? "animate-enter" : "animate-leave"
    } max-w-md w-full bg-white shadow-2xl rounded-[2rem] pointer-events-auto flex ring-1 ring-black ring-opacity-5 overflow-hidden border border-rose-100`}
  >
    <div className="flex-1 w-0 p-5">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <div className="h-14 w-14 rounded-2xl bg-rose-500 flex items-center justify-center text-white shadow-lg shadow-rose-200">
            <HiSparkles size={28} />
          </div>
        </div>
        <div className="ml-4 flex-1">
          <p className="text-base font-black text-rose-950 uppercase tracking-tight">
            Order Placed!
          </p>
          <p className="mt-1 text-xs font-bold text-rose-900/30 uppercase tracking-wider">
            Your order has been successfully confirmed.
          </p>
        </div>
      </div>
    </div>
    <div className="flex items-center pr-4">
      <button
        onClick={() => toast.dismiss(t.id)}
        className="px-4 py-2 text-xs font-black text-rose-500 hover:text-rose-600 uppercase tracking-widest focus:outline-none"
      >
        Close
      </button>
    </div>
  </div>
);

const calculatePostalCharges = (totalWeightGrams) => {
  if (totalWeightGrams <= 0) return 0;
  if (totalWeightGrams <= 500) return 50.0;
  if (totalWeightGrams <= 1000) return 100;
  if (totalWeightGrams <= 2000) return 150.0;
  return 250.0;
};

const Cart = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [walletPoints, setWalletPoints] = useState(0);
  const [useWallet, setUseWallet] = useState(false);
  const [walletUsed, setWalletUsed] = useState(0);
  const [showCheckout, setShowCheckout] = useState(false);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [paymentMode, setPaymentMode] = useState("ONLINE");
  const [orderCount, setOrderCount] = useState(0);
  const [padBalance, setPadBalance] = useState(0);
  const [couponCode, setCouponCode] = useState("");
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null); // Stores full coupon response data
  const [shippingLoading, setShippingLoading] = useState(false);
  const [indiaPostToken, setIndiaPostToken] = useState(null);
  const [postalCharges, setPostalCharges] = useState(0);

  const IMAGE_BASE_URL = SummaryApi?.productImage?.url?.replace(
    "/api/uploads",
    "",
  );

  const getUserData = useCallback(() => {
    const sessionData = localStorage.getItem("user");
    if (!sessionData) return null;
    try {
      return JSON.parse(sessionData);
    } catch (e) {
      return null;
    }
  }, []);

  const getUserId = useCallback(() => {
    const user = getUserData();
    return user ? user.user_id || user.id || user : null;
  }, [getUserData]);
const user = getUserData();
  const isSubscriber = user?.is_subscriber === 1;

  const subtotal = useMemo(
    () =>
      cartItems.reduce((acc, i) => {
        const price = parseFloat(i.price) || 0;
        const qty = parseInt(i.quantity) || 0;
        return acc + price * qty;
      }, 0),
    [cartItems],
  );

  const totalWeight = cartItems.reduce(
    (acc, item) => acc + parseFloat(item.unit?.weight || 0) * item.quantity,
    0,
  );
  const isSyncing = React.useRef(false);
 const fetchShippingTariff = useCallback(() => {
  // If cart is empty, charges are 0
  if (totalWeight <= 0) {
    setPostalCharges(0);
    return;
  }

  if (isSubscriber) {
    // If Subscriber and has placed at least 1 order previously
    if (orderCount > 0) {
      setPostalCharges(50.0);
    } else {
      // First order for subscriber is usually Free
      setPostalCharges(0);
    }
  } else {
    // Non-Subscriber: Always use the weight-based tier calculation
    setPostalCharges(calculatePostalCharges(totalWeight));
  }
}, [totalWeight, isSubscriber, orderCount]);

// Update the useEffect to trigger whenever weight or subscriber status changes
useEffect(() => {
  fetchShippingTariff();
}, [fetchShippingTariff]);

  const getNimbusToken  = async () => {
    try {
      const res = await fetch(SummaryApi.indiaPostLogin.url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: "sureshit@oakyaitservices.com", 
          password: "Eveya@123", 
        }),
      });
      const result = await res.json();
      if (result.success) return result.data.access_token;
    } catch (err) {
      console.error("India Post Login Failed", err);
    }
    return null;
  };
  
  const fetchIndiaPostTariff = useCallback(async () => {
    if (totalWeight <= 0 || !selectedAddressId) return;

    const selectedAddr = addresses.find(
      (a) => a.address_id === selectedAddressId,
    );
    if (!selectedAddr?.pincode) return;

    setShippingLoading(true);
    try {
      const token = await getNimbusToken ();

      // Construct Query Params
      const params = new URLSearchParams({
        "product-code": "SP",
        weight: Math.ceil(totalWeight), // India Post uses Grams
        "source-pincode": "400001", // YOUR Warehouse Pincode
        "destination-pincode": selectedAddr.pincode,
        length: "10", // Default or from product data
        width: "10",
        height: "5",
        INS: "0",
        POD: "NO",
      });

      const res = await fetch(
        `${SummaryApi.getSpeedPostTariff.url}?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const result = await res.json();
      if (result.success) {
        // Update the postal charges state
        setPostalCharges(result.total_with_tax);
      }
    } catch (err) {
      console.error("Tariff Fetch Error", err);
      // Fallback to local calculation if API fails
      setPostalCharges(50.0);
    } finally {
      setShippingLoading(false);
    }
  }, [totalWeight, selectedAddressId, addresses]);
 
  const syncCartItems = async (userId, guestItems) => {
    if (isSyncing.current) return false;
    isSyncing.current = true;

    try {
      for (const item of guestItems) {
        const productId = item.product_id || item.id || item._id;
        const unitId = item.product_unit_id || item.unitId || item.unit_id;
        const qty = item.quantity || item.qty || 1;

        if (!productId) {
          console.warn("Skipping guest item: No product ID found", item);
          continue;
        }
        // 1. First, fetch the product details to get the name
        const prodRes = await fetch(SummaryApi.getActiveProductById.url, {
          method: SummaryApi.getActiveProductById.method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ product_id: productId }),
        });
        const prodJson = await prodRes.json();

        // Get the name from the response, or fallback to a placeholder
        const productName = prodJson.status
          ? prodJson.data.product_title
          : "Product";

        // 2. Now sync to the database with the required 'product_name' field
        const res = await fetch(SummaryApi.addToCart.url, {
          method: SummaryApi.addToCart.method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: userId,
            product_id: productId,
            product_unit_id: unitId,
            quantity: qty,
            product_name: productName, // <--- ADDED THIS FIELD TO FIX THE ERROR
            product_type: item.product_type || "general", // Optional: Add product type if needed
          }),
        });

        const result = await res.json();
        if (!result.status) {
          console.error("Sync failed for item:", productId, result.message);
        }
      }

      // 3. ONLY clear session storage after the loop finishes
      sessionStorage.removeItem("guest_cart");
      window.dispatchEvent(new Event("cartUpdate"));
      return true;
    } catch (error) {
      console.error("Sync Error:", error);
      return false;
    } finally {
      isSyncing.current = false;
    }
  };

  const fetchPadBalance = useCallback(async () => {
    const userId = getUserId();
    const user = getUserData();
    if (!userId || user?.is_subscriber !== 1) return;
    try {
      const res = await fetch(SummaryApi.pads_count_balance.url, {
        method: SummaryApi.pads_count_balance.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      const result = await res.json();
      if (result.status && result.data) {
        setPadBalance(Number(result.data.pads_count || 0));
      }
    } catch (err) {
      console.error("Pad balance fetch error", err);
    }
  }, [getUserId, getUserData]);
  const fetchCartData = useCallback(async () => {
    setLoading(true);
    const userId = getUserId();
    const user = getUserData();
    const isSubscriber = user?.is_subscriber === 1;
    let rawCartData = [];

    try {
      const guestData = sessionStorage.getItem("guest_cart");

      // 1. Sync Guest Items first
      if (userId && guestData) {
        const parsedGuest = JSON.parse(guestData);
        if (parsedGuest.length > 0) {
          const syncSuccess = await syncCartItems(userId, parsedGuest);
          if (syncSuccess)
            await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }

      // 2. Fetch the data
      if (userId) {
        const cartRes = await fetch(SummaryApi.getCartById.url, {
          method: SummaryApi.getCartById.method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId }),
        });
        const cartJson = await cartRes.json();
        if (cartJson.status) rawCartData = cartJson.data || [];
      } else {
        const guestDataLocal = sessionStorage.getItem("guest_cart");
        rawCartData = guestDataLocal ? JSON.parse(guestDataLocal) : [];
      }

      // 3. Enrich the data (Mapping product details)
      const detailedItems = await Promise.all(
        rawCartData.map(async (cartItem) => {
          try {
            const currentQty = Number(cartItem.quantity || cartItem.qty || 0);
            const prodRes = await fetch(SummaryApi.getActiveProductById.url, {
              method: SummaryApi.getActiveProductById.method,
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ product_id: cartItem.product_id }),
            });
            const prodJson = await prodRes.json();

            const dbProd = prodJson.status ? prodJson.data : {};
            let unit = null;

            if (cartItem.product_unit_id) {
              const unitRes = await fetch(
                SummaryApi.product_unit_admin_retrieve_by_id.url,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    product_unit_id: cartItem.product_unit_id,
                  }),
                },
              );
              const unitJson = await unitRes.json();
              if (unitJson.status) unit = unitJson.data;
            }

            const isCombo =
              cartItem.product_type === "combo" || cartItem.type === "combo";
            let basePrice = 0;

            if (isCombo) {
              // For combos, trust the price stored in the cart record
              basePrice = parseFloat(
                cartItem.price || cartItem.product_price || 0,
              );

              if (basePrice === 0) {
                const nameStr =
                  cartItem.product_name ||
                  cartItem.combo_name ||
                  dbProd.product_title ||
                  "";
                const leafMatch = nameStr.match(/(\d+)\s*Leaf/i);
                const flexMatch = nameStr.match(
                  /(\d+)\s*(Flex|Eveya Active Flex)/i,
                );

                const leafQty = leafMatch ? parseInt(leafMatch[1]) || 0 : 0;
                const flexQty = flexMatch ? parseInt(flexMatch[1]) || 0 : 0;

                // Re-calculate: Leaf @ ₹12 (Sub price), Flex @ ₹22 (Based on 440/20)
                basePrice = leafQty * 12 + flexQty * 22;
              }
            } else {
              // For regular products, use your existing logic
              const isLeaf =
                dbProd.product_title?.toLowerCase().includes("leaf") ||
                dbProd.product_title?.toLowerCase().includes("sakhi");

              basePrice =
                unit?.price || dbProd.selling_price || cartItem.price || 0;

              if (isSubscriber && isLeaf) {
                basePrice = Number(unit?.pack_size || 1) * 12;
              }
            }

            return {
              ...cartItem,
              product_type: isCombo
                ? "combo"
                : cartItem.product_type || "general",
              name:
                cartItem.product_name ||
                cartItem.combo_name ||
                dbProd.product_title ||
                "Product",
              image: dbProd.product_image1
                ? `${IMAGE_BASE_URL}/${dbProd.product_image1}`
                : "/placeholder-product.png",
              unit: unit || cartItem.unit,
              price: basePrice,
            };
          } catch (err) {
            const nameStr =
              cartItem.product_name || cartItem.combo_name || "Custom Combo";
            const leafMatch = nameStr.match(/(\d+)\s*Leaf/i);
            const flexMatch = nameStr.match(
              /(\d+)\s*(Flex|Eveya Active Flex)/i,
            );
            const leafQty = leafMatch ? parseInt(leafMatch[1]) || 0 : 0;
            const flexQty = flexMatch ? parseInt(flexMatch[1]) || 0 : 0;

            return {
              ...cartItem,
              quantity: Number(cartItem.quantity || cartItem.qty || 0),
              name: nameStr,
              image: "/placeholder-product.png",
              price:
                leafQty * 12 + flexQty * 22 || parseFloat(cartItem.price) || 0,
              unit: cartItem.unit || { weight: 0, pack_size: 1 },
            };
          }
        }),
      );

      setCartItems(detailedItems.filter((item) => item !== null));
    } catch (err) {
      toast.error("Could not load cart");
    } finally {
      setLoading(false);
    }
  }, [getUserId, getUserData, IMAGE_BASE_URL]);

  const handleUpdateQuantity = async (item, delta) => {
    const newQty = item.quantity + delta;
    if (newQty < 1 || isUpdating) return;
    const userId = getUserId();
    setIsUpdating(true);
    try {
      if (userId && item.cart_id) {
        await fetch(SummaryApi.cartUpdate.url, {
          method: SummaryApi.cartUpdate.method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ cart_id: item.cart_id, quantity: newQty }),
        });
      } else {
        const guestCart = JSON.parse(
          sessionStorage.getItem("guest_cart") || "[]",
        );
        const updated = guestCart.map((i) =>
          i.product_id === item.product_id &&
          String(i.product_unit_id) === String(item.product_unit_id)
            ? { ...i, quantity: newQty }
            : i,
        );
        sessionStorage.setItem("guest_cart", JSON.stringify(updated));
      }
      setCartItems((prev) =>
        prev.map((i) =>
          i.product_id === item.product_id &&
          String(i.product_unit_id) === String(item.product_unit_id)
            ? { ...i, quantity: newQty }
            : i,
        ),
      );
      window.dispatchEvent(new Event("cartUpdate"));
    } catch (err) {
      toast.error("Update failed");
    } finally {
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    const fetchWallet = async () => {
      const userId = getUserId();
      if (!userId) return;
      try {
        const res = await fetch(SummaryApi.wallet_points.url, {
          method: SummaryApi.wallet_points.method || "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId }),
        });
        const result = await res.json();
        if (result.status === true)
          setWalletPoints(Number(result.data?.wallet_points || 0));
      } catch (err) {
        setWalletPoints(0);
      }
    };
    fetchWallet();
  }, [getUserId]);
  useEffect(() => {
    if (selectedAddressId && totalWeight > 0) {
      fetchShippingTariff();
    }
  }, [selectedAddressId, totalWeight, fetchIndiaPostTariff]);
  const confirmDelete = async () => {
    if (!itemToDelete || isUpdating) return;
    const userId = getUserId();
    setIsUpdating(true);
    setShowConfirm(false);
    try {
      if (userId && itemToDelete.cart_id) {
        await fetch(SummaryApi.cartDelete.url, {
          method: SummaryApi.cartDelete.method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ cart_id: itemToDelete.cart_id }),
        });
      } else {
        const guestCart = JSON.parse(
          sessionStorage.getItem("guest_cart") || "[]",
        );
        const filtered = guestCart.filter(
          (item) => item.product_id !== itemToDelete.product_id,
        );
        sessionStorage.setItem("guest_cart", JSON.stringify(filtered));
      }
      toast.custom(
        (t) => <RemovedToast t={t} productName={itemToDelete.name} />,
        { duration: 3000, position: "bottom-center" },
      );
      setCartItems((prev) =>
        prev.filter(
          (item) =>
            !(
              item.product_id === itemToDelete.product_id &&
              item.product_unit_id === itemToDelete.product_unit_id
            ),
        ),
      );
      window.dispatchEvent(new Event("cartUpdate"));
    } catch (error) {
      toast.error("Remove failed");
    } finally {
      setIsUpdating(false);
      setItemToDelete(null);
    }
  };
 

  const fetchUserAddresses = useCallback(async () => {
    const userId = getUserId();
    if (!userId) return;
    try {
      const res = await fetch(SummaryApi.getUserAddressbyId.url, {
        method: SummaryApi.getUserAddressbyId.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      const result = await res.json();
      if (result.status && Array.isArray(result.data)) {
        setAddresses(result.data);
        const def =
          result.data.find((a) => a.is_default === 1) || result.data[0];
        if (def) setSelectedAddressId(def.address_id);
      }
    } catch (err) {
      console.error("Address error", err);
    }
  }, [getUserId]);

  const fetchOrderCount = useCallback(async () => {
    const userId = getUserId();
    if (!userId) return;
    try {
      const res = await fetch(SummaryApi.userOrderCount.url, {
        method: SummaryApi.userOrderCount.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      const result = await res.json();
      if (result.status) {
        setOrderCount(Number(result.total_orders || 0)); // Optionally store in session for other pages
        localStorage.setItem("total_orders", result.total_orders);
      }
    } catch (err) {
      console.error("Error fetching order count:", err);
    }
  }, [getUserId]);
  const handleAddressSync = useCallback((newList) => {
    setAddresses(newList);
  }, []);

  const handleAddressSelect = useCallback((id) => {
    setSelectedAddressId(id);
  }, []);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchCartData();
    // fetchUserAddresses();
    fetchOrderCount();
    fetchPadBalance();
  }, [fetchCartData, fetchOrderCount, fetchPadBalance]);

  

  // Split items into Leaf/Sakhi and Non-Leaf
  const leafPadsInCart = cartItems.filter(
    (item) =>
      item.name?.toLowerCase().includes("leaf") ||
      item.name?.toLowerCase().includes("sakhi"),
  );

  const nonLeafSubtotal = cartItems
    .filter(
      (item) =>
        item.product_type !== "combo" &&
        !(
          item.name?.toLowerCase().includes("leaf") ||
          item.name?.toLowerCase().includes("sakhi")
        ),
    )
    .reduce(
      (acc, item) => acc + parseFloat(item.price || 0) * item.quantity,
      0,
    );
  // 1. Calculate the subtotal for items eligible for the subscriber % discount
  const eligiblePercentageSubtotal = cartItems.reduce((acc, item) => {
    const isCombo = item.product_type === "combo" || item.type === "combo";
    const isLeafProduct =
      item.name?.toLowerCase().includes("leaf") ||
      item.name?.toLowerCase().includes("sakhi");

    if (isCombo) {
      // For Combos, extract the "Flex" portion via name parsing
      const name = item.name || "";
      const flexMatch = name.match(/(\d+)\s*(Flex|Eveya Active Flex)/i);
      const flexQty = flexMatch ? parseInt(flexMatch[1]) : 0;

      // Flex value is qty * 22. This portion gets the % discount.
      const flexValueInCombo = flexQty * 22 * item.quantity;
      return acc + flexValueInCombo;
    } else if (!isLeafProduct) {
      // Regular Non-Leaf products get full price added to discountable subtotal
      return acc + parseFloat(item.price || 0) * item.quantity;
    }
    return acc;
  }, 0);

  let leafPadValueDeduction = 0;
  let totalLeafPadsRequested = 0;

  if (isSubscriber) {
    let currentBalance = padBalance;
    cartItems.forEach((item) => {
      const isLeaf =
        item.name?.toLowerCase().includes("leaf") ||
        item.name?.toLowerCase().includes("sakhi");
      const isCombo = item.product_type === "combo";
      if (isLeaf && !isCombo) {
        const packSize = Number(item.unit?.pack_size || 1);
        const rowPads = item.quantity * packSize;
        const padsToCover = Math.min(rowPads, currentBalance);
        leafPadValueDeduction += padsToCover * 12; // Assuming 12 is the unit price deducted
        currentBalance -= padsToCover;
        totalLeafPadsRequested += rowPads;
      }
    });
  }

  const padsUsedFromBalance = Math.min(totalLeafPadsRequested, padBalance);

  const subInfo = getSubscriptionDetails(); // Used for getting the % percentage
  const duration = String(user?.plan_duration || "").toLowerCase();

  let subscriptionDiscountPercent = 0;
  if (isSubscriber) {
    if (duration.includes("3")) subscriptionDiscountPercent = 18;
    else if (duration.includes("6")) subscriptionDiscountPercent = 22;
    else if (duration.includes("12") || duration.includes("year"))
      subscriptionDiscountPercent = 25;
    else subscriptionDiscountPercent = subInfo.discountPercent || 0;
  }
  // Apply % discount ONLY to non-leaf items
  const subscriptionDiscount = isSubscriber
    ? (eligiblePercentageSubtotal * subscriptionDiscountPercent) / 100
    : 0;

  // Postal Charges Logic
  const weightTiers = [
    { limit: 500, price: 50.0 },
    { limit: 1000, price: 80.1 },
    { limit: 2000, price: 100.0 },
    { limit: 5000, price: 115.0 },
  ];
  const currentTierIndex = weightTiers.findIndex((t) => totalWeight <= t.limit);
  const nextTier = weightTiers[currentTierIndex + 1];
  const progressPercent = Math.min(
    (totalWeight / (nextTier?.limit || 2000)) * 100,
    100,
  );
  const isBestValue = totalWeight > 500 && totalWeight <= 1000;
  const weightMessage =
    totalWeight <= 500
      ? `Shop more to drop shipping to ₹50.00!`
      : totalWeight <= 1000
        ? "✨ Best value shipping rate!"
        : `Add few more items to stay in ₹${nextTier?.price} bracket.`;
  // let postalCharges = 0;


  const couponDiscount = appliedCoupon
    ? Number(appliedCoupon.discount_amount || 0)
    : 0;
  const totalBeforeWallet =
    subtotal - leafPadValueDeduction - subscriptionDiscount + postalCharges;
  const walletDeduction = useWallet
    ? Math.min(50, walletPoints, totalBeforeWallet)
    : 0;
  const finalTotal = Math.max(
    0,
    totalBeforeWallet - walletDeduction - couponDiscount,
  );

  const handlePlaceOrder = async () => {
    const userId = getUserId();
    if (!selectedAddressId) return toast.error("Select address");
    const addr = addresses.find((a) => a.address_id === selectedAddressId);
    setIsPlacingOrder(true);

    try {
      const userDetailsRes = await fetch(SummaryApi.userDetailsById.url, {
        method: SummaryApi.userDetailsById.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });

      const userDetailsData = await userDetailsRes.json();

      if (!userDetailsData.status) {
        throw new Error("Could not retrieve user profile for payment.");
      }

      // Extract the real email and mobile
      const profile = userDetailsData.data;
      const realEmail = profile.user_email || profile.email;
      const realMobile =
        profile.user_mobile || profile.mobile_number || addr.user_mobile;

      // Validate for ZohoPay requirements
      if (!realEmail || !realMobile) {
        toast.error(
          "Please ensure your profile has an email and mobile number.",
        );
        setIsPlacingOrder(false);
        return;
      }

      // 1. Prepare the complete order payload
      const orderData = {
        user_id: userId,
        user_name: addr.user_name,
        mobile_number: addr.user_mobile,
        order_address: `${addr.flat_number}, ${addr.street_name}, ${addr.city} - ${addr.pincode}`,
        order_amount: subtotal,
        delivery_charges: postalCharges,
        total_amount: finalTotal,
        // total_amount: 12,
        order_date: new Date().toISOString().split("T")[0],
        order_date_time: new Date()
          .toISOString()
          .replace("T", " ")
          .split(".")[0],
        payment_mode: paymentMode,
        wallet_deduct_amount: walletDeduction,
        discount: subscriptionDiscount + leafPadValueDeduction + couponDiscount,
        coupon_code: appliedCoupon ? appliedCoupon.coupon_code : null,
        cartItems: cartItems.map((item) => ({
          product_id: item.product_id,
          product_unit_id:
            item.product_unit_id || item.unit?.product_unit_id || null,
          quantity: item.quantity,
        })),
      };

      if (paymentMode === "ONLINE") {
        // --- ONLINE PAYMENT LOGIC ---
        toast.loading("Preparing secure payment...");

        // We do NOT call placeOrder here.
        // We send orderData to createPaymentIntent so the backend can save it
        // ONLY after payment is confirmed.
        const paymentRes = await fetch(SummaryApi.createPaymentIntent.url, {
          method: SummaryApi.createPaymentIntent.method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...orderData, // Send all details to the payment processor
            amount: Number(finalTotal),
            // amount: 12,
            email: realEmail,
            phone: realMobile,
          }),
        });

        const paymentData = await paymentRes.json();
        toast.dismiss();

        if (paymentData.success && paymentData.checkout_url) {
          localStorage.setItem("pendingOrder", JSON.stringify(orderData));

          // Redirect to Gateway (Cart remains full in DB until backend completes order)
          window.location.href = paymentData.checkout_url;
        } else {
          toast.error(paymentData.message || "Payment gateway error.");
        }
      } else {
        // --- COD LOGIC ---
        const res = await fetch(SummaryApi.placeOrder.url, {
          method: SummaryApi.placeOrder.method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...orderData, order_status: "placed" }),
        });

        const result = await res.json();
        if (result.status) {
          // Handle Pad Deduction for COD
          if (padsUsedFromBalance > 0) {
            await fetch(SummaryApi.Order_withdraw_pads.url, {
              method: SummaryApi.Order_withdraw_pads.method,
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                user_id: userId,
                withdraw_pads_count: padsUsedFromBalance,
              }),
            });
          }

          toast.custom((t) => <OrderPlacedToast t={t} />, {
            duration: 4000,
            position: "top-center",
          });

          window.dispatchEvent(new Event("cartUpdate"));
          setTimeout(() => navigate("/order-success"), 2000);
        } else {
          toast.error(result.message || "Order failed");
        }
      }
    } catch (err) {
      console.error("Order Error:", err);
      toast.error("Error processing order");
    } finally {
      setIsPlacingOrder(false);
    }
  };

  const handleWalletToggle = (e) => {
    setUseWallet(e.target.checked);
    if (e.target.checked)
      setWalletUsed(Math.min(50, walletPoints, totalBeforeWallet));
    else setWalletUsed(0);
  };

  const handleApplyCoupon = async () => {
    const userId = getUserId();
    const user = getUserData();
    if (!userId) return toast.error("Please login to apply coupon");
    if (!couponCode) return toast.error("Enter a coupon code");

    setIsValidatingCoupon(true);
    try {
      const response = await fetch(SummaryApi.validate_coupon.url, {
        method: SummaryApi.validate_coupon.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          coupon_code: couponCode,
          order_amount: subtotal, // Validating against subtotal
          organization_id: user?.organization_id || 0,
        }),
      });

      const result = await response.json();

      if (result.status) {
        setAppliedCoupon(result.data); // Assuming data contains discount_amount
        toast.success(result.message || "Coupon applied successfully!");
      } else {
        setAppliedCoupon(null);
        toast.error(result.message || "Invalid coupon code");
      }
    } catch (error) {
      console.error("Coupon Error:", error);
      toast.error("Failed to validate coupon");
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    toast.success("Coupon removed");
  };

  let runningBalance = padBalance;

  if (loading)
    return (
      <div className="min-h-screen bg-[#FFF0F3] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-rose-500"></div>
      </div>
    );
  if (cartItems.length === 0) return <EmptyCart />;

  return (
    <div className="min-h-screen bg-[#FFF0F3] py-20 px-6">
      <AnimatePresence>
        {showConfirm && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowConfirm(false)}
              className="absolute inset-0 bg-rose-950/40 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="relative bg-white rounded-[2.5rem] p-8 max-w-sm w-full shadow-2xl text-center"
            >
              <HiOutlineExclamationTriangle
                size={48}
                className="mx-auto text-rose-500 mb-4"
              />
              <h3 className="text-xl font-black text-rose-950 uppercase mb-2">
                Remove Item?
              </h3>
              <p className="text-rose-900/50 mb-6 text-sm">
                Remove {itemToDelete?.name}?
              </p>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setShowConfirm(false)}
                  className="py-3 font-bold text-rose-400"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="bg-rose-500 text-white py-3 rounded-xl font-black"
                >
                  Remove
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
          <div>
            <h1 className="text-5xl font-black text-rose-950 uppercase tracking-tighter">
              Your Box
            </h1>
            <p className="text-rose-900/40 font-bold uppercase text-xs tracking-widest mt-2">
              {cartItems.length} Items{" "}
            </p>
          </div>
          <Link
            to="/products"
            className="bg-white text-rose-600 font-black px-6 py-3 rounded-2xl shadow-sm border border-rose-100 flex items-center gap-2 text-sm hover:bg-rose-50 transition-all w-fit"
          >
            <HiOutlineArrowLeft /> CONTINUE SHOPPING
          </Link>
        </div>

        {isSubscriber && padBalance > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 bg-gradient-to-r from-rose-500 to-rose-600 p-5 rounded-[2rem] text-white shadow-xl shadow-rose-200 flex items-center justify-between"
          >
            <div className="flex items-center gap-4">
              <div className="bg-white/20 p-3 rounded-2xl">
                <HiOutlineTicket size={28} />
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest opacity-80">
                  Subscriber Benefit Applied
                </p>
                <p className="text-base font-bold">
                  You have {padBalance} Leaf pads in balance.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {!subInfo.isEligible && !isSubscriber && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10 bg-gradient-to-br from-white to-rose-50/50 border border-white rounded-[2.5rem] p-4 md:p-8 shadow-xl shadow-rose-200/50 relative overflow-hidden"
          >
            <div className="relative z-10">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-6">
                <div className="flex items-center gap-4">
                  <div
                    className={`h-14 w-14 rounded-2xl flex items-center justify-center shadow-lg ${isBestValue ? "bg-green-500 text-white" : "bg-rose-500 text-white"}`}
                  >
                    <RiShoppingBag3Line size={28} />
                  </div>
                  <div>
                    <h4 className="text-xl font-black text-rose-950 uppercase tracking-tight">
                      Box Capacity
                    </h4>
                    <p className="text-sm font-bold text-rose-900/50 italic">
                      {weightMessage}
                    </p>
                  </div>
                </div>
                <div className="bg-white/80 backdrop-blur-md border border-rose-100 px-6 py-3 rounded-2xl text-center md:text-right">
                  <p className="text-[10px] font-black text-rose-400 uppercase tracking-widest mb-1">
                    Postal Charges
                  </p>
                  <p className="text-2xl font-black text-rose-950">
                    ₹{postalCharges.toFixed(2)}
                  </p>
                </div>
              </div>
              <div className="relative">
                <div className="h-4 w-full bg-rose-100/50 rounded-full overflow-hidden border border-rose-100">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progressPercent}%` }}
                    className={`h-full rounded-full ${isBestValue ? "bg-gradient-to-r from-green-400 to-green-600" : "bg-gradient-to-r from-rose-400 to-rose-600"}`}
                  />
                </div>
                <div className="flex justify-between mt-2 px-1">
                  <span className="text-[9px] font-black text-rose-300 uppercase">
                    0g
                  </span>
                  <span
                    className={`text-[9px] font-black uppercase ${totalWeight > 500 ? "text-green-500" : "text-rose-300"}`}
                  >
                    500g (Target)
                  </span>
                  <span className="text-[9px] font-black text-rose-300 uppercase">
                    1kg+
                  </span>
                </div>
              </div>
            </div>
            <div className="absolute top-0 right-0 opacity-5 pointer-events-none">
              <HiSparkles size={200} />
            </div>
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => {
              const isCombo = item.product_type === "combo";
              const isLeaf =
                item.name?.toLowerCase().includes("leaf") ||
                item.name?.toLowerCase().includes("sakhi");

              const packSize = Number(item.unit?.pack_size || 1);
              const totalPadsInRow = item.quantity * packSize;

              let rowDisplayTotal = item.price * item.quantity;

              if (!isCombo && isSubscriber && isLeaf && runningBalance > 0) {
                const padsCovered = Math.min(totalPadsInRow, runningBalance);
                const padsUncovered = totalPadsInRow - padsCovered;
                rowDisplayTotal = padsUncovered * 12;
                runningBalance -= padsCovered;
              }

              return (
                <motion.div
                  key={`${item.product_id}-${item.product_unit_id}`}
                  layout
                  className="bg-white/70 backdrop-blur-md border border-white rounded-[2rem] p-5 flex flex-col md:flex-row items-center gap-6 shadow-sm"
                >
                  <div className="w-20 h-20 bg-white rounded-2xl overflow-hidden flex-shrink-0 border border-rose-50">
                    <img
                      src={item.image || "/placeholder-product.png"}
                      alt={item.name}
                      className="w-full h-full object-contain p-2"
                    />
                  </div>
                  <div className="flex-1 text-center md:text-left">
                    <h3 className="text-base font-bold text-rose-950 leading-tight">
                      {item.name}
                    </h3>
                    {item.unit && (
                      <p className="text-xs font-bold text-rose-500 mt-1">
                        Pack {item.unit.pack_size}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-3 bg-rose-50 rounded-xl p-1 border border-rose-100">
                      <button
                        onClick={() => handleUpdateQuantity(item, -1)}
                        className="w-8 h-8 flex items-center justify-center text-rose-400 hover:bg-white rounded-lg transition-colors"
                      >
                        <HiOutlineMinus />
                      </button>
                      <span className="font-black text-rose-950 min-w-[24px] text-center">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => handleUpdateQuantity(item, 1)}
                        className="w-8 h-8 flex items-center justify-center text-rose-400 hover:bg-white rounded-lg transition-colors"
                      >
                        <HiOutlinePlus />
                      </button>
                    </div>
                    <div className="text-right min-w-[80px]">
                      <p className="text-sm font-black text-rose-950">
                        ₹{rowDisplayTotal.toFixed(0)}
                      </p>

                      {isCombo ? (
                        <p className="text-[10px] text-rose-400 font-bold uppercase">
                          Custom Combo
                        </p>
                      ) : (
                        rowDisplayTotal === 0 &&
                        isSubscriber &&
                        isLeaf && (
                          <p className="text-[10px] text-emerald-500 font-bold uppercase">
                            Balance Covered
                          </p>
                        )
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setItemToDelete(item);
                        setShowConfirm(true);
                      }}
                      className="p-2 text-rose-300 hover:text-rose-500 transition-colors"
                    >
                      <HiOutlineTrash size={18} />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="lg:col-span-1">
            <div className="bg-rose-950 rounded-[3rem] p-8 text-white shadow-2xl sticky top-32">
              <h2 className="text-2xl font-black uppercase mb-8 border-b border-white/10 pb-4 tracking-tighter">
                Summary
              </h2>
              <div className="space-y-4 mb-8">
                <div className="flex justify-between text-rose-200/60 font-medium text-sm">
                  <span>Items Subtotal</span>
                  <span className="text-white">₹{subtotal.toFixed(2)}</span>
                </div>

                {leafPadValueDeduction > 0 && (
                  <div className="flex justify-between text-emerald-400 font-bold text-sm">
                    <span className="flex items-center gap-1">
                      <HiOutlineTicket size={14} /> Pad Balance Covered
                    </span>
                    <span>-₹{leafPadValueDeduction.toFixed(2)}</span>
                  </div>
                )}

                {/* ONLY SHOW DISCOUNT IF > 0 */}
                {subscriptionDiscount > 0 ? (
                  <div className="flex justify-between text-yellow-400 font-bold text-sm">
                    <span className="flex items-center gap-1">
                      <HiSparkles size={14} /> Subscriber Discount
                    </span>
                    <span>-₹{subscriptionDiscount.toFixed(2)}</span>
                  </div>
                ) : (
                  // SHOW HINT IF USER IS A SUBSCRIBER BUT ONLY HAS LEAF PADS
                  isSubscriber && (
                    <div className="p-3 rounded-xl bg-white/5 border border-dashed border-white/20">
                      <p className="text-[10px] text-rose-300 leading-tight italic">
                        Tip: Add other products besides Leaf pads to unlock your
                        extra subscriber discount!
                      </p>
                    </div>
                  )
                )}

                {/* Coupon in Checkout Summary */}
                {appliedCoupon && (
                  <div className="flex justify-between text-blue-600 font-bold text-sm">
                    <span>Coupon Discount</span>
                    <span>-₹{couponDiscount.toFixed(2)}</span>
                  </div>
                )}
<div className="flex justify-between text-rose-200/60 font-medium text-sm">
  <span>
    Postal Charges
    {isSubscriber && (
      <span className="text-[10px] block text-rose-400">
        {orderCount === 0
          ? "(First Order Bonus)"
          : "(Subscriber Flat Rate)"}
      </span>
    )}
  </span>
  <span className="text-white">
    {postalCharges === 0 ? "FREE" : `₹${postalCharges.toFixed(2)}`}
  </span>
</div>

                <div className="mb-6">
                  <p className="text-[10px] uppercase font-bold text-rose-200/60 ml-1 mb-2">
                    Have a Coupon?
                  </p>
                  <div className="relative flex gap-2">
                    <div className="relative flex-1">
                      <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-rose-200/60" />
                      <input
                        type="text"
                        placeholder="COUPON CODE"
                        value={couponCode}
                        disabled={!!appliedCoupon}
                        onChange={(e) =>
                          setCouponCode(e.target.value.toUpperCase())
                        }
                        className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-xs font-bold text-white focus:outline-none focus:ring-2 focus:ring-rose-500 transition-all placeholder:text-rose-200/30 disabled:opacity-50"
                      />
                    </div>
                    {appliedCoupon ? (
                      <button
                        onClick={handleRemoveCoupon}
                        className="bg-rose-500/20 hover:bg-rose-500/40 text-rose-200 p-3 rounded-xl transition-all"
                      >
                        <HiOutlineXMark size={20} />
                      </button>
                    ) : (
                      <button
                        onClick={handleApplyCoupon}
                        disabled={isValidatingCoupon || !couponCode}
                        className="bg-rose-500 hover:bg-rose-400 px-4 py-2 rounded-xl text-[10px] font-black uppercase transition-all disabled:bg-white/10"
                      >
                        {isValidatingCoupon ? "..." : "Apply"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {/* Add this inside the Summary box, above the Total Payable div */}
              {walletPoints > 0 && (
                <div className="mb-6 p-4 rounded-2xl bg-white/5 border border-white/10">
                  <label className="flex items-center justify-between cursor-pointer">
                    <div className="flex flex-col">
                      <span className="text-xs font-black uppercase tracking-widest text-rose-200">
                        Use Wallet Points
                      </span>
                      <span className="text-[10px] text-rose-300/60">
                        Available: ₹{walletPoints} (Max ₹50 per order)
                      </span>
                    </div>
                    <input
                      type="checkbox"
                      checked={useWallet}
                      onChange={handleWalletToggle}
                      className="w-5 h-5 rounded border-rose-500 text-rose-500 focus:ring-rose-500 accent-rose-500"
                    />
                  </label>
                  {useWallet && (
                    <div className="mt-2 text-right text-xs font-bold text-emerald-400">
                      -₹{walletDeduction.toFixed(2)} Applied
                    </div>
                  )}
                </div>
              )}

              {/* Existing Total Payable Div */}
              <div className="pt-6 border-t border-white/10 flex justify-between items-end mb-8">
                <div>
                  <p className="text-[10px] font-black text-rose-200/40 uppercase">
                    Total Payable
                  </p>
                  <p className="text-4xl font-black">
                    ₹{finalTotal.toFixed(2)}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowCheckout(true)}
                className="w-full bg-rose-500 hover:bg-rose-400 py-5 rounded-[2rem] font-black text-lg shadow-xl uppercase tracking-widest transition-all"
              >
                Checkout Now
              </button>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showCheckout && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowCheckout(false)}
              className="absolute inset-0 bg-rose-950/60 backdrop-blur-md"
            />
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              className="relative bg-white rounded-[2.5rem] w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col md:flex-row shadow-2xl"
            >
              <div className="hidden md:flex w-1/3 bg-rose-50 p-10 flex-col">
                <h3 className="text-lg font-black text-rose-950 uppercase mb-6">
                  Your Order
                </h3>
                <div className="flex-1 overflow-y-auto space-y-5 pr-2">
                  {cartItems.map((item) => (
                    <div
                      key={`${item.product_id}-${item.product_unit_id}`}
                      className="flex gap-3 items-start"
                    >
                      <img
                        src={item.image || "/placeholder-product.png"}
                        className="w-12 h-12 rounded-xl bg-white object-contain border border-rose-100"
                        alt={item.name}
                      />
                      <div className="text-[10px] flex-1">
                        <p className="font-bold text-rose-950 truncate w-32">
                          {item.name}
                        </p>
                        <p className="text-rose-500">Qty: {item.quantity}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="pt-6 border-t border-rose-200 mt-6 space-y-2">
                  <div className="flex justify-between text-sm text-rose-900/60">
                    <span>Subtotal</span>
                    <span>₹{subtotal.toFixed(2)}</span>
                  </div>
                  {leafPadValueDeduction > 0 && (
                    <div className="flex justify-between text-rose-900/60 font-bold text-sm">
                      <span className="flex items-center gap-1">
                        {" "}
                        Pad Balance Covered
                      </span>
                      <span>-₹{leafPadValueDeduction.toFixed(2)}</span>
                    </div>
                  )}
                  {/* Subscriber Benefit Row */}
                  {subInfo.isEligible && (
                    <div className="flex justify-between text-rose-900/60 font-bold text-sm">
                      <span className="flex items-center gap-1">
                        {" "}
                        Subscriber Discount
                      </span>
                      <span>-₹{subscriptionDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-rose-900/60 font-medium text-sm">
                    <span>
                      Postal Charges
                      {subInfo.isEligible && (
                        <span className="text-[10px] block text-yellow-500">
                          {orderCount === 0
                            ? "(Subscriber First Order Bonus)"
                            : "(Subscriber Flat Rate)"}
                        </span>
                      )}
                    </span>
                    <span className="text-rose-900/60">
                      {postalCharges === 0
                        ? "FREE"
                        : `₹${postalCharges.toFixed(2)}`}
                    </span>
                  </div>
                  {walletPoints > 0 && useWallet && (
                    <div className="flex justify-between text-green-700">
                      <span>Wallet Applied</span>
                      <span>-₹{walletDeduction.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-rose-950 font-black text-xl pt-2 border-t border-rose-100">
                    <span>Total</span>
                    <span>
                      {/* ₹12 */}
                      {finalTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex-1 p-8 md:p-12 overflow-y-auto">
                <div className="flex justify-between items-center mb-8">
                  <h2 className="text-2xl font-black text-rose-950 uppercase">
                    Checkout
                  </h2>
                  <button
                    onClick={() => setShowCheckout(false)}
                    className="text-rose-500 font-bold"
                  >
                    Close
                  </button>
                </div>
                {!getUserId() ? (
                  <div className="text-center py-10">
                    <p className="mb-4 text-rose-900 font-bold">
                      Please login to continue
                    </p>
                    <Link
                      to="/login"
                      className="bg-rose-500 text-white px-8 py-3 rounded-xl font-black inline-block"
                    >
                      Login Now
                    </Link>
                    <Link
                      to="/signup"
                      className="ml-4 bg-gray-200 text-gray-700 px-8 py-3 rounded-xl font-bold inline-block"
                    >
                      Sign Up Now
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-10">
                    <UserAddress
                      userId={getUserId()}
                      onAddressSelect={handleAddressSelect}
                      onAddressUpdate={handleAddressSync}
                    />
                    <div>
                      <h4 className="text-sm font-black text-rose-950 uppercase mb-4">
                        Payment Method
                      </h4>
                      <div
                        onClick={() => setPaymentMode("ONLINE")}
                        className="p-4 rounded-2xl border-2 cursor-pointer transition-all flex items-center gap-3 border-rose-50"
                      >
                        <div className="w-5 h-5 rounded-full border-2 border-rose-500 flex items-center justify-center">
                          <div className="w-2.5 h-2.5 bg-rose-500 rounded-full" />
                        </div>
                        <div>
                          <p className="text-xs font-black text-rose-950">
                            Online Payment
                          </p>
                          <p className="text-[10px] text-rose-900/40">
                            UPI, Cards, Netbanking
                          </p>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={handlePlaceOrder}
                      disabled={!selectedAddressId || isPlacingOrder}
                      className={`w-full py-5 rounded-[2rem] bg-rose-500 text-white font-black text-xl shadow-xl transition-all disabled:bg-gray-200`}
                    >
                      {isPlacingOrder
                        ? "Placing Order..."
                        : `Confirm Order • ₹${finalTotal.toFixed(2)}`}
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

const EmptyCart = () => (
  <div className="min-h-screen bg-[#FFF0F3] flex items-center justify-center p-6 text-center">
    <div>
      <RiShoppingBag3Line size={80} className="mx-auto text-rose-200 mb-6" />
      <h2 className="text-4xl font-black text-rose-950 uppercase mb-6">
        Box is empty
      </h2>
      <Link
        to="/products"
        className="bg-rose-500 text-white px-10 py-4 rounded-2xl font-black inline-block"
      >
        Start Shopping
      </Link>
    </div>
  </div>
);

export default Cart;
